import type {
  PredictionContract,
  PredictionEvent,
  PredictionTeam,
} from '@/services/prediction/types'

/**
 * Port of cdc-web `src/utils/color.ts` 的合约/队伍配色机器——home 卡片胜率下
 * 划线（WinChanceBar）与 fallback badge 颜色的单一来源。保留本区原有的
 * `hexToRgb`/`calculateBrightness`（CompetitorLogo FallbackBadge 用），并新增：
 *
 *   - COLOR_PALETTE + parseTheColorMap：按合约分配冲突消解后的颜色。
 *     关键规则：**无 team 的合约（F1 车手、FX strike）按 `id.localeCompare`
 *     排序后的索引从调色板取色**，保证每行必有色、且跨事件稳定（不依赖 API
 *     数组顺序）。two-tone 联盟（NBA/MLB/…）直接用队伍色不做测试。
 *   - buildEventColorMaps：match 事件取 moneyline section 合约（开相似度测试），
 *     league 取顶层 `event.contracts`（不开）。
 *   - rescueTeamColor：按 scheme 钳亮度（dark: l ≥ 0.62）重算 fontColor。
 *     cdc-market 是 dark-only，调用方硬编码 `'dark'`。
 *
 * 未移植（本区无消费者）：withAlpha / getColorWithOpacity / resolveRowAccent /
 * readableTeamColor（hsl 变体）。
 */

/* intentional: data-viz — contract/outcome series palette.
 * 调色板与 cdc-web 一致（OG-10018 移除纯白，银灰 #B0B7BC 占中性槽位）。 */
const COLOR_PALETTE = [
  '#FF4433', // Red
  '#FF7919', // Orange
  '#C46BFF', // Purple
  '#33AAFF', // Blue
  '#B4F500', // Green
  '#F58426', // Bright Orange
  '#FFD200', // Gold
  '#B0B7BC', // Silver
  '#00B2A9', // Teal
  '#8CD2F4', // Sky Blue
  '#FFB81C', // Amber
]

const TWO_TONE_EVENT_KINDS = new Set([
  'CBB',
  'CFB',
  'DEB',
  'EPL',
  'ESP',
  'FIFA',
  'FRL1',
  'ITSA',
  'MLB',
  'MLS',
  'NBA',
  'NFL',
  'NHL',
  'UCL',
])

export interface TeamColor {
  /** 缺色时为 undefined（cdc-web `Team.color` 类型必需、靠 BE 履约；cdc-market
   * 类型可选。调色板不对缺色编兜底色——未命中即无色，与 cdc-web 行为对齐）。 */
  color?: string
  secondaryColor?: string
  fontColor: string
  isNational?: boolean
  logoUrl?: string
}

export interface ColorOptions {
  contrast_threshold: number // 对比度阈值 (0-255)
  similar_threshold: number // 相似度阈值
  contrast_test_needed: boolean
  similar_test_needed: boolean
  tie_color: string // 平局（Draw）色
  event_kind?: string // two-tone 处理按 event kind 判定
}

export interface ColorMaps {
  contractsColorMap: Map<string, TeamColor>
  teamsColorMap: Map<string, TeamColor>
}

export type GetTeamColorFn = (
  teamId?: string | null,
  contractId?: string | null,
) => TeamColor | null

export const DEFAULT_COLOR_OPTIONS: ColorOptions = {
  contrast_threshold: 70,
  similar_threshold: 80,
  contrast_test_needed: true,
  similar_test_needed: true,
  tie_color: 'var(--content-secondary)',
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
  const expanded = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expanded)
  if (!result) return null
  return {
    r: Number.parseInt(result[1], 16),
    g: Number.parseInt(result[2], 16),
    b: Number.parseInt(result[3], 16),
  }
}

/** 感知亮度（YIQ），无法解析时返回 0。 */
export function calculateBrightness(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
}

/** RGB (0-255) → HSL（h 度，s/l ∈ [0,1]）。 */
function rgbToHsl(r: number, g: number, b: number): { h: number; s: number; l: number } {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l } // achromatic (grey)

  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case rn:
      h = (gn - bn) / d + (gn < bn ? 6 : 0)
      break
    case gn:
      h = (bn - rn) / d + 2
      break
    default:
      h = (rn - gn) / d + 4
  }
  return { h: h * 60, s, l }
}

/** HSL（h 度，s/l ∈ [0,1]）→ RGB (0-255)。rgbToHsl 的逆。 */
function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  if (s === 0) {
    const v = Math.round(l * 255)
    return { r: v, g: v, b: v } // achromatic (grey)
  }
  const hue2rgb = (p: number, q: number, t: number): number => {
    let tt = t
    if (tt < 0) tt += 1
    if (tt > 1) tt -= 1
    if (tt < 1 / 6) return p + (q - p) * 6 * tt
    if (tt < 1 / 2) return q
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6
    return p
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const hn = h / 360
  return {
    r: Math.round(hue2rgb(p, q, hn + 1 / 3) * 255),
    g: Math.round(hue2rgb(p, q, hn) * 255),
    b: Math.round(hue2rgb(p, q, hn - 1 / 3) * 255),
  }
}

/** RGB (0-255) → `#rrggbb`。 */
function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v)))
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

/**
 * 亮度钳制（readableTeamColorHex 的内部实现）：BE 队伍色无主题变体，任意品牌
 * 色在 dark 卡面上可能暗到不可读。保留色相（队伍身份），只把明度钳进当前
 * scheme 的可读带（light ≤ 0.45，dark ≥ 0.62）；带内的颜色原样穿过。无效
 * 输入返回 undefined。
 */
function clampReadableLightness(
  hex: string | undefined,
  scheme: 'light' | 'dark',
): { h: number; s: number; l: number } | undefined {
  if (!hex) return undefined
  const rgb = hexToRgb(hex)
  if (!rgb) return undefined

  const { h, s, l } = rgbToHsl(rgb.r, rgb.g, rgb.b)
  const clampedL = scheme === 'light' ? Math.min(l, 0.45) : Math.max(l, 0.62)
  return { h, s, l: clampedL }
}

/**
 * 钳制后的 hex 版本（hex 进 hex 出），供下游按 hex 解析的链路消费。无效
 * 输入原样返回（caller 保持无损）。
 */
function readableTeamColorHex(hex: string, scheme: 'light' | 'dark'): string {
  const clamped = clampReadableLightness(hex, scheme)
  if (!clamped) return hex
  const rgb = hslToRgb(clamped.h, clamped.s, clamped.l)
  return rgbToHex(rgb.r, rgb.g, rgb.b)
}

/** 两色欧氏距离（相似度度量）；任一不可解析返回 Infinity。 */
function calculateColorDistance(hex1: string, hex2: string): number {
  const rgb1 = hexToRgb(hex1)
  const rgb2 = hexToRgb(hex2)
  if (!rgb1 || !rgb2) return Infinity

  return Math.sqrt(
    Math.pow(rgb1.r - rgb2.r, 2) + Math.pow(rgb1.g - rgb2.g, 2) + Math.pow(rgb1.b - rgb2.b, 2),
  )
}

/** 对比度测试：亮度 ≥ 阈值。 */
function passesContrastTest(color: string, threshold: number): boolean {
  return calculateBrightness(color) >= threshold
}

/** color1 与 color2 是否过于相似。 */
function isTwoColorSimilar(color1: string, color2: string, threshold: number): boolean {
  return calculateColorDistance(color1, color2) < threshold
}

/** color 是否与任一已有色过于相似。 */
function isSimilarToExistingColors(
  color: string,
  existingColors: string[],
  threshold: number,
): boolean {
  return existingColors.some(existingColor => isTwoColorSimilar(color, existingColor, threshold))
}

/**
 * 从调色板选可用色。优先级：1. 最接近 preferredColor（原色）2. 最接近
 * fallbackColor（secondary）3. 第一个通过测试的未用色 4. 第一个未用色。
 */
function selectFromPalette(
  usedColors: Set<string>,
  existingColors: string[],
  options: ColorOptions,
  preferredColor?: string,
  fallbackColor?: string,
): string {
  const availableColors = COLOR_PALETTE.filter(color => !usedColors.has(color.toUpperCase()))

  if (availableColors.length === 0) {
    return COLOR_PALETTE[0]
  }

  const passesAllTests = (color: string): boolean => {
    if (options.contrast_test_needed && !passesContrastTest(color, options.contrast_threshold)) {
      return false
    }
    if (
      options.similar_test_needed &&
      isSimilarToExistingColors(color, existingColors, options.similar_threshold)
    ) {
      return false
    }
    return true
  }

  if (preferredColor) {
    const validColors = availableColors.filter(passesAllTests)
    if (validColors.length > 0) {
      return validColors.reduce(
        (closest, current) =>
          calculateColorDistance(preferredColor, current) <
          calculateColorDistance(preferredColor, closest)
            ? current
            : closest,
        validColors[0],
      )
    }
  }

  if (fallbackColor) {
    const validColors = availableColors.filter(passesAllTests)
    if (validColors.length > 0) {
      return validColors.reduce(
        (closest, current) =>
          calculateColorDistance(fallbackColor, current) <
          calculateColorDistance(fallbackColor, closest)
            ? current
            : closest,
        validColors[0],
      )
    }
  }

  const validColor = availableColors.find(passesAllTests)
  if (validColor) {
    return validColor
  }

  return availableColors[0]
}

/** 单支队伍选色：primary → secondary 候选依次过对比度/相似度测试，全挂走调色板。 */
function selectColorForTeam(
  team: PredictionTeam,
  existingColors: string[],
  usedColors: Set<string>,
  options: ColorOptions,
): string {
  const candidates = [team.color, team.secondary_color]

  for (const candidateColor of candidates) {
    if (!candidateColor) continue

    if (options.contrast_test_needed) {
      if (!passesContrastTest(candidateColor, options.contrast_threshold)) {
        continue
      }
    }

    if (options.similar_test_needed) {
      if (isSimilarToExistingColors(candidateColor, existingColors, options.similar_threshold)) {
        continue
      }
    }

    return candidateColor
  }

  return selectFromPalette(usedColors, existingColors, options, team.color, team.secondary_color)
}

function getFontColorByBackground(color: string): string {
  return calculateBrightness(color) > 128 ? '#000' : '#fff' // intentional: 队伍色底上的对比字色
}

/**
 * 对已解析的 TeamColor 按当前 scheme 做可读性兜底（OG-10018）：钳
 * color/secondaryColor 亮度（hex 进 hex 出），fontColor 按兜底后的色重算。
 * 带内颜色原样穿过；非 hex 的 tie 色（`var(--content-secondary)`）不重算
 * fontColor；null 输入原样返回。cdc-market 为 dark-only，调用方传 'dark'。
 */
export function rescueTeamColor(
  teamColor: TeamColor | null,
  scheme: 'light' | 'dark',
): TeamColor | null {
  if (!teamColor) return null
  const color = teamColor.color ? readableTeamColorHex(teamColor.color, scheme) : teamColor.color
  const secondaryColor = teamColor.secondaryColor
    ? readableTeamColorHex(teamColor.secondaryColor, scheme)
    : teamColor.secondaryColor
  const fontColor =
    color?.startsWith('#') && teamColor.color
      ? getFontColorByBackground(color)
      : teamColor.fontColor
  return { ...teamColor, color, secondaryColor, fontColor }
}

function makeTeamColor(
  color: string | undefined,
  secondaryColor?: string,
  team?: PredictionTeam,
): TeamColor {
  return {
    color,
    secondaryColor,
    fontColor: color ? getFontColorByBackground(color) : '#fff',
    isNational: team?.team_type === 'national',
    logoUrl: team?.logo_url,
  }
}

/**
 * two-tone match 事件（NBA/MLB/FIFA…）：平局用中性色；非平局合约按 team_id
 * 去重（一个 moneyline section 可能合并 2-way/3-way 盘导致同队出现两次），
 * 取前两队在 primary/secondary 的 4 组色组合里找第一组互不相似的直用。
 */
function parseTheColorMapForTwoToneEvent(
  contracts: PredictionContract[],
  options: ColorOptions,
): ColorMaps {
  const teamsColorMap = new Map<string, TeamColor>()
  const contractsColorMap = new Map<string, TeamColor>()

  const tieContract = contracts.find(contract => contract.prediction_outcome_type === 'Draw')
  const tieTeamColor: TeamColor = {
    color: options.tie_color,
    fontColor: '#000', // intentional: 浅色 tie badge 上的对比字色
    logoUrl: tieContract?.team?.logo_url,
  }
  const nonTieContracts = contracts
    .filter(contract => contract.prediction_outcome_type !== 'Draw')
    .reduce<PredictionContract[]>((acc, contract) => {
      const existingIndex = contract.team?.id
        ? acc.findIndex(c => c.team?.id === contract.team?.id)
        : -1
      if (existingIndex === -1) {
        acc.push(contract)
        return acc
      }
      const existing = acc[existingIndex]
      const existingHasQuote = !!existing.yes && !!existing.chance
      const candidateHasQuote = !!contract.yes && !!contract.chance
      if (!existingHasQuote && candidateHasQuote) {
        acc[existingIndex] = contract
      }
      return acc
    }, [])

  if (tieContract) {
    const tieTeamId = tieContract.team?.id
    if (tieTeamId) {
      teamsColorMap.set(tieTeamId, tieTeamColor)
    }
    contractsColorMap.set(tieContract.id, tieTeamColor)
  }

  if (nonTieContracts.length === 0) {
    return { contractsColorMap, teamsColorMap }
  }

  if (nonTieContracts.length === 1) {
    const contract = nonTieContracts[0]
    if (contract.team) {
      const teamColor = makeTeamColor(
        contract.team.color,
        contract.team.secondary_color,
        contract.team,
      )
      teamsColorMap.set(contract.team.id, teamColor)
      contractsColorMap.set(contract.id, teamColor)
    }
    return { contractsColorMap, teamsColorMap }
  }

  const firstContract = nonTieContracts[0]
  const secondContract = nonTieContracts[1]

  if (firstContract.team && secondContract.team) {
    const t1 = firstContract.team
    const t2 = secondContract.team

    // 缺色不编兜底色（与 cdc-web 对齐）：两色组合缺一即不做相似度测试，
    // 直接取第一组（可能含 undefined，下游按无色渲染）。
    const c1 = t1.color
    const s1 = t1.secondary_color
    const c2 = t2.color
    const s2 = t2.secondary_color

    const combinations: [
      string | undefined,
      string | undefined,
      string | undefined,
      string | undefined,
    ][] = [
      [c1, s1, c2, s2],
      [c1, s1, s2, c2],
      [s1, c1, c2, s2],
      [s1, c1, s2, c2],
    ]

    const [primaryColor1, secondaryColor1, primaryColor2, secondaryColor2] =
      combinations.find(
        ([a, , b]) => a && b && !isTwoColorSimilar(a, b, options.similar_threshold),
      ) ?? combinations[0]

    const teamColor1 = makeTeamColor(primaryColor1, secondaryColor1, t1)
    const teamColor2 = makeTeamColor(primaryColor2, secondaryColor2, t2)

    contractsColorMap.set(firstContract.id, teamColor1)
    teamsColorMap.set(firstContract.team.id, teamColor1)
    contractsColorMap.set(secondContract.id, teamColor2)
    teamsColorMap.set(secondContract.team.id, teamColor2)
  }

  return { contractsColorMap, teamsColorMap }
}

/**
 * 合约/队伍颜色分配。返回 contractsColorMap（contract.id → TeamColor）与
 * teamsColorMap（team.id → TeamColor）。
 *
 * 规则：
 *   - Draw 合约 → 中性 tie_color；
 *   - 无 team 合约（FX strike / F1 车手）→ 按 `id.localeCompare` 排序后的
 *     索引取 COLOR_PALETTE[idx % 11]，保证每行有色且跨事件稳定；
 *   - two-tone league 事件 → 队伍色直用（不做测试）；
 *   - 其余 → selectColorForTeam（对比度/相似度测试，挂则调色板兜底）。
 */
export function parseTheColorMap(
  contracts?: PredictionContract[],
  options: Partial<ColorOptions> = DEFAULT_COLOR_OPTIONS,
): ColorMaps {
  const parsedOptions = { ...DEFAULT_COLOR_OPTIONS, ...options }
  const isTwoToneEvent =
    !!parsedOptions.event_kind && TWO_TONE_EVENT_KINDS.has(parsedOptions.event_kind)

  if (parsedOptions.similar_test_needed && isTwoToneEvent) {
    return parseTheColorMapForTwoToneEvent(contracts || [], parsedOptions)
  }

  const contractsColorMap = new Map<string, TeamColor>()
  const teamsColorMap = new Map<string, TeamColor>()

  if (!contracts || contracts.length === 0) return { contractsColorMap, teamsColorMap }

  const setColor = (contractId: string, teamId: string | undefined, teamColor: TeamColor) => {
    contractsColorMap.set(contractId, teamColor)
    if (teamId) teamsColorMap.set(teamId, teamColor)
  }

  const tieContract = contracts.find(contract => contract.prediction_outcome_type === 'Draw')
  if (tieContract) {
    setColor(tieContract.id, tieContract.team?.id, {
      color: parsedOptions.tie_color,
      fontColor: '#000', // intentional: 浅色 tie badge 上的对比字色
      logoUrl: tieContract.team?.logo_url,
    })
  }

  const existingColors: string[] = []
  const usedColors = new Set<string>()

  const sortedNonTieContracts = contracts
    .filter(contract => contract.prediction_outcome_type !== 'Draw')
    .sort((a, b) => a.id.localeCompare(b.id))

  sortedNonTieContracts.forEach((contract, contractIdx) => {
    const teamId = contract.team?.id

    if (!contract.team) {
      const color = COLOR_PALETTE[contractIdx % COLOR_PALETTE.length]
      setColor(contract.id, teamId, makeTeamColor(color))
      return
    }

    if (isTwoToneEvent) {
      setColor(
        contract.id,
        teamId,
        makeTeamColor(contract.team.color, contract.team.secondary_color, contract.team),
      )
      return
    }

    const finalColor = selectColorForTeam(contract.team, existingColors, usedColors, parsedOptions)
    usedColors.add(finalColor.toUpperCase())
    existingColors.push(finalColor)
    setColor(contract.id, teamId, makeTeamColor(finalColor, undefined, contract.team))
  })

  return { contractsColorMap, teamsColorMap }
}

/**
 * 为事件构建规范的队伍/合约颜色映射（跨卡片/详情页同色同源）。match 取
 * moneyline section 合约（开相似度测试）；league 取顶层 `event.contracts`
 *（不开）。幂等性来自三个固定输入：合约切片、按事件类型的 options、
 * parseTheColorMap 的 id 排序分配——均不依赖 API 数组顺序。
 */
export function buildEventColorMaps(event: PredictionEvent): ColorMaps {
  const isMatchEvent = event.event_type === 'match'
  const contracts = isMatchEvent
    ? event.sections?.find(section => section.market_type_name === 'moneyline')?.contracts
    : event.contracts
  return parseTheColorMap(contracts, {
    similar_test_needed: isMatchEvent,
    event_kind: event.event_kind,
  })
}
